import {FormEvent, useId, useState} from "react";
import {Badge, Button, ButtonGroup, Card, Form, Modal, ProgressBar} from "react-bootstrap";
import {DocumentationApi} from "../../../api/DocumentationApi";
import {DocumentationImage} from "../../../api/models";
import {ApiError} from "../../../api/http";
import {ErrorAlert} from "../../../components/ErrorAlert";
import {ConfirmModal} from "../../../components/ConfirmModal";
import {SubmitButton} from "../../../components/SubmitButton";
import {DOC_IMAGE_MAX_BYTES, DOC_IMAGE_TYPES, docImageMarkup, docImageName, isValidDocImageName} from "./docImages";

interface Props {
    productId: string;
    images: DocumentationImage[];
    onChanged(): void;
    // Puts the image at the editor's cursor.
    onInsert(image: DocumentationImage): void;
}

// Why the file can't be a documentation image, if it can't.
const fileProblem = (file: File) => !DOC_IMAGE_TYPES.includes(file.type) ? `${file.name}: use a PNG or JPEG image.`
    : file.size > DOC_IMAGE_MAX_BYTES ? `${file.name}: the image must be 10 MB or smaller.`
    : undefined;

// A product's documentation images, on the CDN. Each has a name, unique within the product, which documentation
// written elsewhere uses (<img src="doc-image:{name}">), so the same HTML works on dev and prod. Uploading names each
// file after itself; a file named like an image already here replaces its picture, unless that's turned off.
export function DocumentationImageLibrary({productId, images, onChanged, onInsert}: Props) {
    const [files, setFiles] = useState<File[]>([]);
    const [replaceSameName, setReplaceSameName] = useState(true);
    const [progress, setProgress] = useState<{ index: number, fraction: number }>();
    const [problems, setProblems] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const [formKey, setFormKey] = useState(0);
    const [copied, setCopied] = useState<string>();
    const [editing, setEditing] = useState<DocumentationImage>();
    const [deleting, setDeleting] = useState<DocumentationImage>();

    const upload = async (e: FormEvent) => {
        e.preventDefault();
        if (!files.length) {
            return;
        }
        setBusy(true);
        setError(undefined);
        const found: string[] = [];
        try {
            for (const [index, file] of files.entries()) {
                const problem = fileProblem(file);
                if (problem) {
                    found.push(problem);
                    continue;
                }
                setProgress({ index, fraction: 0 });
                const name = docImageName(file.name);
                const existing = replaceSameName ? images.find(x => x.name === name) : undefined;
                try {
                    await DocumentationApi.uploadImage(productId, file, fraction => setProgress({ index, fraction }),
                        existing ? { replaces: existing.id } : { name });
                } catch (err) {
                    found.push(`${file.name}: ${err instanceof ApiError && err.code === 'image_rejected'
                        ? "it isn't a PNG or JPEG of at most 8000 pixels a side." : 'it failed to upload.'}`);
                }
            }
            setFiles([]);
            setFormKey(x => x + 1); // clears the file input
        } catch (err) {
            setError(err);
        } finally {
            setProblems(found);
            setBusy(false);
            setProgress(undefined);
            onChanged();
        }
    };

    const copy = async (image: DocumentationImage) => {
        try {
            await navigator.clipboard.writeText(docImageMarkup(image));
            setCopied(image.id);
        } catch (err) {
            setError(err);
        }
    };

    const remove = async (image: DocumentationImage) => {
        setBusy(true);
        setError(undefined);
        try {
            await DocumentationApi.deleteImage(productId, image.id, image.inUse);
            setDeleting(undefined);
            onChanged();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="mt-4">
            <Card.Header>Images</Card.Header>
            <Card.Body>
                <p className="small text-body-secondary">
                    Images for the documentation, PNG or JPEG. In HTML, show one with <code>&lt;img src="doc-image:<em>name</em>" alt="…"&gt;</code>:
                    names are the same on every stage when the same files are uploaded, so the same HTML works on dev and prod.
                    An image stays here until it's deleted, used or not.
                </p>
                <ErrorAlert error={error} onClose={() => setError(undefined)}/>
                {problems.length > 0 && (
                    <div className="alert alert-warning" role="alert">
                        Some files weren't added:
                        <ul className="mb-0">{problems.map(x => <li key={x}>{x}</li>)}</ul>
                    </div>
                )}
                <Form key={formKey} onSubmit={upload} className="d-flex flex-wrap gap-3 align-items-center mb-3">
                    <Form.Control type="file" multiple accept={DOC_IMAGE_TYPES.join(',')} aria-label="Image files" className="w-auto flex-grow-1"
                                  disabled={busy} onChange={e => setFiles([...((e.target as HTMLInputElement).files ?? [])])}/>
                    <Form.Check id="doc-images-replace" label="Replace images with the same name" checked={replaceSameName}
                                disabled={busy} onChange={e => setReplaceSameName(e.target.checked)}/>
                    <SubmitButton busy={busy}>Upload {files.length > 1 ? `${files.length} images` : 'image'}</SubmitButton>
                </Form>
                {progress && (
                    <ProgressBar className="mb-3" now={(progress.index + progress.fraction) / files.length * 100}
                                 label={`${progress.index + 1} of ${files.length}`} aria-label="Uploading"/>
                )}
                {images.length === 0 && <p className="text-body-secondary mb-0">No images yet.</p>}
                <div className="d-flex flex-wrap gap-3">
                    {images.map(image => (
                        <div key={image.id} className="product-image-tile">
                            <img src={image.src} alt={image.altText} width={image.width} height={image.height} className="img-thumbnail mb-1"/>
                            <div className="small font-monospace text-truncate" title={image.name}>{image.name}</div>
                            <div className="small text-body-secondary">
                                {image.width}×{image.height}
                                {image.inUse && <Badge bg="success" className="ms-2">In use</Badge>}
                            </div>
                            <div className="small text-truncate text-body-secondary mb-1" title={image.altText}>{image.altText || 'No description'}</div>
                            <ButtonGroup size="sm">
                                <Button variant="outline-secondary" disabled={busy} onClick={() => void copy(image)}
                                        aria-label={`Copy the HTML for ${image.name}`} title="Copy the HTML">
                                    <i className={`bi ${copied === image.id ? 'bi-clipboard-check' : 'bi-clipboard'}`} aria-hidden="true"></i>
                                </Button>
                                <Button variant="outline-secondary" disabled={busy} onClick={() => onInsert(image)}
                                        aria-label={`Insert ${image.name} at the cursor`} title="Insert at the cursor">
                                    <i className="bi bi-box-arrow-in-down" aria-hidden="true"></i>
                                </Button>
                                <Button variant="outline-secondary" disabled={busy} onClick={() => setEditing(image)} aria-label={`Edit ${image.name}`} title="Edit">
                                    <i className="bi bi-pencil" aria-hidden="true"></i>
                                </Button>
                                <Button variant="outline-danger" disabled={busy} onClick={() => setDeleting(image)} aria-label={`Delete ${image.name}`} title="Delete">
                                    <i className="bi bi-trash" aria-hidden="true"></i>
                                </Button>
                            </ButtonGroup>
                        </div>
                    ))}
                </div>
            </Card.Body>
            {editing && (
                <ImageModal productId={productId} image={editing} onHide={() => setEditing(undefined)}
                            onSaved={() => { setEditing(undefined); onChanged(); }}/>
            )}
            <ConfirmModal show={!!deleting} title={`Delete ${deleting?.name ?? 'this image'}?`} confirmLabel="Delete" busy={busy}
                          onConfirm={() => void remove(deleting!)} onCancel={() => setDeleting(undefined)}>
                {deleting && (
                    <>
                        <img src={deleting.src} alt={deleting.altText} className="img-thumbnail product-image-preview mb-2"/>
                        {deleting.inUse && (
                            <p className="text-danger mb-0">
                                The saved documentation shows this image. Deleting it takes it out of the documentation too, and HTML
                                that refers to <code>doc-image:{deleting.name}</code> will lose it the next time it's pasted in.
                            </p>
                        )}
                    </>
                )}
            </ConfirmModal>
        </Card>
    );
}

// Renames an image, changes its description, or replaces its picture (keeping its name, so the HTML needn't change).
function ImageModal({productId, image, onHide, onSaved}: { productId: string, image: DocumentationImage, onHide(): void, onSaved(): void }) {
    const titleId = useId();
    const [name, setName] = useState(image.name);
    const [altText, setAltText] = useState(image.altText);
    const [file, setFile] = useState<File>();
    const [validated, setValidated] = useState(false);
    const [progress, setProgress] = useState<number>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const nameProblem = isValidDocImageName(name.trim()) ? undefined
        : 'Lower case letters and digits, with single dashes between words, up to 64 characters.';
    const fileError = file ? fileProblem(file) : undefined;

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (nameProblem || fileError) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            if (name.trim() !== image.name || altText.trim() !== image.altText) {
                await DocumentationApi.updateImage(productId, image.id, { name: name.trim(), altText: altText.trim() });
            }
            if (file) {
                setProgress(0);
                await DocumentationApi.uploadImage(productId, file, setProgress, { replaces: image.id });
            }
            onSaved();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setProgress(undefined);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show onHide={busy ? () => undefined : onHide} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton={!busy}><Modal.Title id={titleId}>Edit image</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error}/>
                    <img src={image.src} alt={image.altText} className="img-thumbnail product-image-preview mb-3"/>
                    <Form.Group className="mb-3" controlId="doc-image-name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control className="font-monospace" value={name} disabled={busy} isInvalid={validated && !!nameProblem}
                                      aria-describedby="doc-image-name-help" onChange={e => setName(e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{nameProblem}</Form.Control.Feedback>
                        <Form.Text id="doc-image-name-help">
                            HTML written elsewhere refers to it by name. The saved documentation keeps showing it when it's renamed.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="doc-image-alt">
                        <Form.Label>Description (alt text)</Form.Label>
                        <Form.Control as="textarea" rows={2} value={altText} disabled={busy} aria-describedby="doc-image-alt-help"
                                      onChange={e => setAltText(e.target.value)}/>
                        <Form.Text id="doc-image-alt-help">Used where the documentation's &lt;img&gt; has no description of its own.</Form.Text>
                    </Form.Group>
                    <Form.Group controlId="doc-image-file">
                        <Form.Label>Replace the picture <span className="text-body-secondary">(optional)</span></Form.Label>
                        <Form.Control type="file" accept={DOC_IMAGE_TYPES.join(',')} disabled={busy} isInvalid={validated && !!fileError}
                                      onChange={e => setFile((e.target as HTMLInputElement).files?.[0])}/>
                        <Form.Control.Feedback type="invalid">{fileError}</Form.Control.Feedback>
                    </Form.Group>
                    {progress !== undefined && <ProgressBar className="mt-3" now={progress * 100} aria-label="Uploading"/>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Save</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
